<template>
  <div id="ssh_keys" class="proxima_nova flex flex-col gap-4">
    <div
      id="ssh_keys_header"
      class="flex flex-row justify-between items-center"
    >
      <h1 class="text-xl font-semibold text_color">SSH Keys</h1>

      <a-button class="primary_medium" @click="showAddDrawer = true">
        Add SSH Key
      </a-button>
    </div>

    <SimpleTable
      :headers="sshKeysTableHeaders"
      :data="tableData"
      :show-actions-column="true"
      :actions="tableActions"
    />

    <AddSSHKeyDrawer
      :visible="showAddDrawer"
      @close="showAddDrawer = false"
      @created="toListKeys"
    />

    <ConfirmationModal
      v-model:visible="showDeleteModal"
      title="Delete SSH Key"
      :message="`Are you sure you want to delete the SSH key '${keyToDelete?.name}'?`"
      :loading="isDeleting"
      type="delete"
      confirm-text="Delete"
      loading-text="Deleting..."
      @confirm="handleDeleteConfirm"
      @cancel="showDeleteModal = false"
    />
  </div>
</template>

<script setup>
import { onMounted, ref, computed } from "vue";
import { message } from "ant-design-vue";
import { formatDateTime } from "@/src/utils/utils.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import { sshKeysTableHeaders, sshKeyTypes } from "@/src/constants/codebaseConstants.js";
import codebaseService from "@/src/api/CodeService.js";
import ConfirmationModal from "@/src/components/ConfirmationModal.vue";
import SimpleTable from "@/src/components/custom_table_components/SimpleTable.vue";
import AddSSHKeyDrawer from "@/src/components/codebase_components/tokens/AddSSHKeyDrawer.vue";

const keysList = ref([]);
const showAddDrawer = ref(false);
const showDeleteModal = ref(false);
const keyToDelete = ref(null);
const isDeleting = ref(false);

const keyTypeLabel = (value) =>
  sshKeyTypes.find((t) => t.value === value)?.label ?? value;

const tableData = computed(() =>
  keysList.value.map((key) => ({
    ...key,
    key_type: keyTypeLabel(key.key_type),
    created_at: formatDateTime(key.created_at || null),
  })),
);

const tableActions = [
  {
    label: "Delete",
    icon: imageNames.DELETE_GREY,
    handler: (record) => {
      keyToDelete.value = record;
      showDeleteModal.value = true;
    },
  },
];

const toListKeys = async () => {
  try {
    const response = await codebaseService.base.sshKeys.list();
    if (response?.success) {
      keysList.value = response?.data || [];
    }
  } catch (error) {
    console.error("An error occurred while listing SSH keys:", error);
    message.error(error?.response?.data?.error || "Failed to fetch SSH keys");
  }
};

const handleDeleteConfirm = async () => {
  try {
    isDeleting.value = true;
    const response = await codebaseService.base.sshKeys.delete(
      keyToDelete.value?.id,
    );
    if (response?.success) {
      message.success("SSH key deleted successfully");
      showDeleteModal.value = false;
      keyToDelete.value = null;
      await toListKeys();
    }
  } catch (error) {
    console.error("An error occurred while deleting SSH key:", error);
    message.error(error?.response?.data?.error || "Failed to delete SSH key");
  } finally {
    isDeleting.value = false;
  }
};

onMounted(() => {
  toListKeys();
});
</script>
